import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { SEO } from '@/components';
import { FiArrowLeft } from 'react-icons/fi';
import { track } from '@/lib/analytics';

// Simple Header for legal pages
function LegalHeader() {
  return (
    <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
      <nav className='max-w-7xl mx-auto px-6 py-4'>
        <div className='flex items-center justify-between'>
          <Link to='/' search={{ hero: undefined }} className='text-white text-2xl font-semibold tracking-tight'>
            Harbor
          </Link>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white/90 hover:text-white transition-colors font-medium'
          >
            <FiArrowLeft />
            Back to Home
          </Link>
        </div>
      </nav>
    </header>
  );
}

const TermsOfService = () => {
  useEffect(() => {
    track('Terms of Service Screen');
  }, []);

  return (
    <div className='bg-white min-h-screen'>
      <SEO
        title='Terms of Service | Harbor'
        description='Review the terms and conditions for using Harbor, the co-parenting app for expense tracking, messaging, and custody scheduling.'
        path='/terms-of-service'
      />
      <LegalHeader />
      <div className='pt-24'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-left'>
          <header className='mb-12 pb-6 border-b border-gray-200'>
            <h1 className='text-4xl font-bold text-gray-900 mb-2'>Terms of Service</h1>
            <p className='text-gray-500 text-sm'>Last Updated: January 2025</p>
          </header>

          <div className='space-y-8 text-gray-600 text-left'>
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>1. Acceptance of Terms</h2>
              <p>
                By accessing and using Harbor ("the App"), you accept and agree to be bound by the terms
                and provisions of this agreement. If you do not agree to these terms, please do not use
                the App.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>2. Description of Service</h2>
              <p className='mb-4'>
                Harbor is a co-parenting coordination platform that provides tools for:
              </p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Tracking and splitting child-related expenses</li>
                <li>Recording payments between co-parents</li>
                <li>Managing shared calendars and schedules</li>
                <li>Facilitating communication between household members</li>
                <li>Storing receipts and expense documentation</li>
              </ul>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>3. Subscription Terms</h2>
              <p className='mb-4'>
                Harbor offers auto-renewable subscription plans to access premium features. By subscribing, you agree to the following terms:
              </p>

              <h3 className='text-xl font-semibold text-gray-800 mb-3 mt-6'>3.1 Billing</h3>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Payment will be charged to your Apple ID account (for iOS), Google Play account (for Android), or the payment method you provide on the web (Stripe) at confirmation of purchase, unless a free trial is offered and you are eligible</li>
                <li>When a free trial is offered on the Annual plan, eligible subscribers are not charged until the trial ends</li>
                <li>Subscription plans include Monthly and Annual options at the prices displayed in the app at the time of purchase</li>
                <li>Prices are subject to change; you will be notified in advance of any price increases</li>
              </ul>

              <h3 className='text-xl font-semibold text-gray-800 mb-3 mt-6'>3.2 Auto-Renewal</h3>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Subscriptions automatically renew unless auto-renew is turned off at least 24 hours before the end of the current billing period</li>
                <li>Your account will be charged for renewal within 24 hours prior to the end of the current period at the same price</li>
                <li>You will be notified of any price changes before your subscription renews at the new price</li>
              </ul>

              <h3 className='text-xl font-semibold text-gray-800 mb-3 mt-6'>3.3 Managing and Canceling Your Subscription</h3>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>You can manage or cancel your subscription at any time through your device's account settings:
                  <ul className='list-disc list-inside ml-6 mt-2 space-y-1'>
                    <li>iOS: Settings → [Your Name] → Subscriptions</li>
                    <li>Android: Google Play Store → Menu → Subscriptions</li>
                    <li>Web: cancel from the Stripe customer portal or receipt for your subscription</li>
                  </ul>
                </li>
                <li>Cancellation will take effect at the end of the current billing period; you will retain access until then</li>
                <li>Deleting the app does not cancel your subscription</li>
              </ul>

              <h3 className='text-xl font-semibold text-gray-800 mb-3 mt-6'>3.4 Refunds</h3>
              <p className='mb-4'>
                No refunds will be provided for the unused portion of any subscription period. For iOS users, refund requests are handled by Apple in accordance with their refund policies. For Android users, refund requests are handled by Google Play in accordance with their refund policies.
              </p>

              <h3 className='text-xl font-semibold text-gray-800 mb-3 mt-6'>3.5 Free Trials</h3>
              <p>
                If offered, free trial periods (including the 7-day / one-week trial on the Annual plan) will automatically convert to paid subscriptions at the end of the trial period unless canceled at least 24 hours before the trial ends. Any unused portion of a free trial will be forfeited when you purchase a subscription.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>4. User Accounts</h2>
              <p className='mb-4'>
                You are responsible for maintaining the confidentiality of your account and password.
                You agree to:
              </p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Provide accurate and complete information when creating your account</li>
                <li>Accept responsibility for all activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Not share your account credentials with others</li>
              </ul>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>5. Household Membership</h2>
              <p className='mb-4'>
                When you create or join a household in Harbor:
              </p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>You agree to share relevant co-parenting information with other household members</li>
                <li>You confirm that you have a legitimate co-parenting relationship with other household members</li>
                <li>You understand that expense records and communications are visible to household members</li>
                <li>You agree to use the platform in good faith for co-parenting coordination</li>
              </ul>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>6. Acceptable Use</h2>
              <p className='mb-4'>You agree not to use Harbor to:</p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Harass, abuse, or harm other users</li>
                <li>Submit false or misleading expense information</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Attempt to gain unauthorized access to other accounts</li>
                <li>Use the service for any purpose other than legitimate co-parenting coordination</li>
              </ul>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>7. Expense Tracking Disclaimer</h2>
              <p>
                Harbor provides tools to help track and split expenses between co-parents. However,
                Harbor does not verify the accuracy of expense entries, does not process actual payments
                between users, and is not responsible for resolving financial disputes between co-parents.
                The App is a record-keeping tool and does not constitute legal or financial advice.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                8. Divorce Document Preparation
              </h2>
              <p>
                Harbor offers self-help tools that prepare official court forms based on the
                information you provide. Harbor is not a law firm, is not your attorney, and does
                not provide legal advice, legal representation, or legal review of your answers.
                The forms Harbor prepares are the official forms published by your state&apos;s
                courts; completing them through Harbor is equivalent to filling them out yourself.
                You are responsible for reviewing every document before signing or filing it, and
                for confirming current requirements with your court. Court outcomes are never
                guaranteed. If you are unsure whether an uncontested divorce is right for your
                situation, consult a family law attorney licensed in your state.
              </p>
              <p className='mt-3'>
                Divorce packet purchases are refundable in full any time before your packet is
                generated. If your state updates its official forms after your purchase, Harbor
                regenerates your packet against the new versions at no charge.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>9. Intellectual Property</h2>
              <p>
                The App and its original content, features, and functionality are owned by Harbor and are
                protected by international copyright, trademark, patent, trade secret, and other
                intellectual property laws.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>10. Limitation of Liability</h2>
              <p>
                In no event shall Harbor, its directors, employees, partners, agents, suppliers, or
                affiliates be liable for any indirect, incidental, special, consequential, or punitive
                damages, including without limitation, loss of profits, data, use, goodwill, or other
                intangible losses, resulting from your access to or use of or inability to access or
                use the App.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>11. Termination</h2>
              <p>
                We may terminate or suspend your account immediately, without prior notice or liability,
                for any reason whatsoever, including without limitation if you breach the Terms. Upon
                termination, your right to use the App will immediately cease.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>12. Changes to Terms</h2>
              <p>
                We reserve the right to modify or replace these Terms at any time. If a revision is
                material, we will try to provide at least 30 days notice prior to any new terms taking
                effect. What constitutes a material change will be determined at our sole discretion.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>13. Contact Us</h2>
              <p>
                If you have any questions about these Terms, please contact us at{' '}
                <a href='mailto:team@harborcoparent.com' className='text-primary hover:underline'>
                  team@harborcoparent.com
                </a>
                .
              </p>
            </section>
          </div>

          <footer className='mt-16 pt-6 border-t border-gray-200 text-left text-gray-400 text-sm'>
            <p>&copy; {new Date().getFullYear()} Harbor. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/terms-of-service')({
  component: TermsOfService,
});

